// src/pages/Settings/master/ManageReceiptTemplate.js
import React, { useState, useEffect } from "react";
import { Row, Col, Button, Input, FormGroup, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { get } from "helpers/api_helper";
import cookieHelper from "helpers/getCookieData";
import { showSuccessAlert } from "pages/utils/alertMessages";
import { openReceiptPrintWindow } from "pages/utils/printHelpers";
import {
  RiReceiptLine,
  RiPrinterLine,
  RiSaveLine,
  RiRefreshLine,
  RiEyeLine,
  RiToothLine,
  RiImageAddLine,
  RiDeleteBinLine,
  RiUpload2Line,
  RiCheckLine,
  RiLayoutMasonryLine,
  RiSettings4Line,
  RiPaletteLine,
} from "react-icons/ri";

const DEFAULT_RECEIPT_THEMES = [
  {
    id: 8,
    type: "receipt",
    theme_id: "official_voucher",
    title: "Official Money Receipt Voucher",
    badge: "Financial Voucher",
    accent_color: "#059669",
    header_title: "OFFICIAL MONEY RECEIPT",
    tagline: "Payment Acknowledgement & Financial Voucher",
    signatory_title: "Accounts Reception Desk",
    notes: "Received with thanks towards specialized dental treatment and clinical services.",
    footer_note: "• This is an authentic computerized digital payment receipt.\n• Thank you for trusting 909 Dental OPD with your smile.",
    description: "Standard receipt voucher with payment method, invoice reference, and cashier signature line.",
    is_active: true,
  },
  {
    id: 9,
    type: "receipt",
    theme_id: "aesthetic_receipt",
    title: "Aesthetic Studio Receipt",
    badge: "Cosmetic & Aligners",
    accent_color: "#4f46e5",
    header_title: "TREATMENT PAYMENT RECEIPT",
    tagline: "Center for Advanced Aesthetic Smile Architecture",
    signatory_title: "Accounts & Billing Desk",
    notes: "Received with thanks towards aesthetic smile transformation and orthodontic treatment.",
    footer_note: "• Computer-generated digital statement.\n• Please preserve this receipt for tax and reimbursement claims.",
    description: "Royal indigo theme suited for high-end aesthetic dental studios and cosmetic cases.",
    is_active: true,
  },
];

const COLOR_PALETTES = [
  { name: "Emerald Mint", value: "#059669" },
  { name: "Royal Indigo", value: "#4f46e5" },
  { name: "Sky Medical Blue", value: "#0284c7" },
  { name: "Modern Purple", value: "#7c3aed" },
  { name: "Executive Slate", value: "#0f172a" },
  { name: "Amber Gold", value: "#d97706" },
];

const MOCK_RECEIPT_DATA = {
  id: 402,
  receipt_no: "REC-909-402",
  billing_no: "INV-DEMO-2026",
  amount: 17000,
  payment_mode: "UPI (GPay / PhonePe)",
  payment_date: new Date(),
  remark: "Full settlement for Root Canal Treatment & Zirconia Crown (#16)",
  patient: {
    title: "Mrs.",
    first_name: "Sunita",
    last_name: "Reddy",
    case_no: "909-P-101",
    mobile: "+91 98765 43210",
  },
};

const ManageReceiptTemplate = () => {
  const { t } = useTranslation();
  const [themesList, setThemesList] = useState(DEFAULT_RECEIPT_THEMES);
  const [config, setConfig] = useState(DEFAULT_RECEIPT_THEMES[0]);
  const [zoomLevel, setZoomLevel] = useState(100);

  const clinic = (() => {
    try {
      const raw = cookieHelper.getCookie("_c");
      return raw ? JSON.parse(atob(raw)) : null;
    } catch (e) {
      return null;
    }
  })();

  const fetchActiveThemes = async () => {
    try {
      const res = await get("/dropdown/document-templates?type=receipt");
      if (res?.success && Array.isArray(res?.body) && res.body.length > 0) {
        setThemesList(res.body);
        const saved = localStorage.getItem("receipt_template_config");
        if (!saved) {
          setConfig(res.body[0]);
        }
      }
    } catch (e) {
      console.warn("Could not load dynamic receipt templates:", e);
    }
  };

  useEffect(() => {
    fetchActiveThemes();
    const saved = localStorage.getItem("receipt_template_config");
    if (saved) {
      try {
        setConfig(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse receipt template config:", e);
      }
    }
  }, []);

  const handleApplyPreset = (preset) => {
    const updated = {
      ...preset,
      logoImage: config.logoImage || preset.logoImage || '',
      signImage: config.signImage || preset.signImage || '',
      accentColor: preset.accent_color || preset.accentColor || "#059669",
      headerTitle: preset.header_title || preset.headerTitle || "OFFICIAL MONEY RECEIPT",
      signatoryTitle: preset.signatory_title || preset.signatoryTitle || "Accounts Reception Desk",
      thanksNote: preset.notes || preset.thanksNote || config.thanksNote,
      footerNote: preset.footer_note || preset.footerNote || config.footerNote,
    };
    setConfig(updated);
    localStorage.setItem("receipt_template_config", JSON.stringify(updated));
    showSuccessAlert(`Applied "${preset.title || preset.name}" Receipt Theme!`);
  };

  const handleImageUpload = (e, fieldType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert("Image size should be less than 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setConfig((prev) => ({ ...prev, [fieldType]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (fieldType) => {
    setConfig((prev) => ({ ...prev, [fieldType]: "" }));
  };

  const handleSave = () => {
    localStorage.setItem("receipt_template_config", JSON.stringify(config));
    showSuccessAlert(t("Money Receipt template settings saved successfully!"));
  };

  const handleReset = () => {
    const defaultTheme = themesList[0] || DEFAULT_RECEIPT_THEMES[0];
    setConfig(defaultTheme);
    localStorage.setItem("receipt_template_config", JSON.stringify(defaultTheme));
    showSuccessAlert(t("Receipt template restored to SuperAdmin active default."));
  };

  const handleTestPrint = () => {
    openReceiptPrintWindow(MOCK_RECEIPT_DATA, clinic);
  };

  const accentColor = config.accent_color || config.accentColor || "#059669";

  return (
    <div className="template-studio-wrapper py-1">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h4 className="font-weight-bold text-slate-800 mb-1 d-flex align-items-center gap-2">
            <RiReceiptLine className="text-primary" />
            {t("Payment Receipt & Voucher Template Settings")}
          </h4>
          <p className="text-muted font-size-12 mb-0">
            {t("Select from SuperAdmin approved receipt layouts and customize your clinic branding, cashier stamp, and notes.")}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button color="light" size="sm" className="border d-flex align-items-center gap-1 font-size-12" onClick={handleReset}>
            <RiRefreshLine /> {t("Reset SuperAdmin Defaults")}
          </Button>
          <Button color="primary" size="sm" className="d-flex align-items-center gap-1 shadow-sm font-size-12 font-weight-semibold" onClick={handleTestPrint}>
            <RiPrinterLine /> {t("Test Print Receipt")}
          </Button>
        </div>
      </div>

      {/* Preset Themes Gallery: Only Active Templates Approved by SuperAdmin */}
      <div className="mb-4">
        <h6 className="font-size-13 font-weight-bold text-dark mb-2.5 d-flex align-items-center gap-1.5">
          <RiLayoutMasonryLine className="text-primary font-size-15" />
          {t("Available Active Receipt Themes (SuperAdmin Approved)")}
        </h6>
        <div className="theme-grid">
          {themesList.map((theme) => {
            const isSelected = config.theme_id === theme.theme_id || config.id === theme.id;
            const themeColor = theme.accent_color || theme.accentColor || "#059669";
            return (
              <div
                key={theme.id || theme.theme_id}
                className={`theme-card-box ${isSelected ? "active-theme" : ""}`}
                onClick={() => handleApplyPreset(theme)}
              >
                <div className="theme-top-row">
                  <span className="theme-color-indicator" style={{ background: themeColor }} />
                  <span
                    className="badge font-size-10"
                    style={{
                      background: isSelected ? `${themeColor}18` : "#f1f5f9",
                      color: isSelected ? themeColor : "#475569",
                      border: `1px solid ${isSelected ? `${themeColor}40` : "#e2e8f0"}`,
                    }}
                  >
                    {theme.badge || "Financial Voucher"}
                  </span>
                </div>
                <div className="theme-card-title">{theme.title || theme.name}</div>
                <div className="theme-card-desc">{theme.description}</div>
                <div className="theme-card-footer">
                  <span className="text-muted font-size-11 font-weight-semibold">
                    {isSelected ? "✓ Clinic Active Choice" : "Click to Select"}
                  </span>
                  <span className="badge bg-light text-dark border font-size-10">Receipt Voucher</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Split Workspace */}
      <Row className="g-4">
        <Col lg={5} md={12}>
          <div className="customizer-card">
            <div className="section-title">
              <RiSettings4Line className="text-primary font-size-16" />
              <span>{t("Clinic Receipt Customization")}</span>
            </div>

            {/* Logo & Signature Uploader */}
            <div className="p-3 mb-4 rounded border bg-light">
              <div className="font-size-12 font-weight-bold text-dark mb-2.5 d-flex align-items-center gap-1.5">
                <RiImageAddLine className="text-primary font-size-15" />
                {t("Clinic Logo & Cashier Stamp")}
              </div>
              <Row className="g-3">
                <Col sm={6}>
                  <Label className="font-size-11 font-weight-semibold text-secondary mb-1">
                    {t("Clinic Logo")}
                  </Label>
                  {config.logoImage ? (
                    <div className="position-relative p-2 bg-white border rounded text-center">
                      <img
                        src={config.logoImage}
                        alt="Logo Preview"
                        style={{ maxHeight: "40px", maxWidth: "100%", objectFit: "contain" }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="btn-icon position-absolute"
                        style={{ top: "-8px", right: "-8px", width: "22px", height: "22px", padding: 0, borderRadius: "50%" }}
                        onClick={() => handleRemoveImage("logoImage")}
                      >
                        <RiDeleteBinLine className="font-size-11" />
                      </Button>
                    </div>
                  ) : (
                    <label className="btn btn-outline-secondary btn-sm font-size-11 mb-0 w-100 d-flex align-items-center justify-content-center gap-1 cursor-pointer">
                      <RiUpload2Line /> {t("Upload Logo")}
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleImageUpload(e, "logoImage")} />
                    </label>
                  )}
                  <small className="text-muted font-size-10 d-block mt-1">PNG/JPG transparent</small>
                </Col>

                <Col sm={6}>
                  <Label className="font-size-11 font-weight-semibold text-secondary mb-1">
                    {t("Cashier Stamp / Sign")}
                  </Label>
                  {config.signImage ? (
                    <div className="position-relative p-2 bg-white border rounded text-center">
                      <img
                        src={config.signImage}
                        alt="Signature Preview"
                        style={{ maxHeight: "40px", maxWidth: "100%", objectFit: "contain" }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="btn-icon position-absolute"
                        style={{ top: "-8px", right: "-8px", width: "22px", height: "22px", padding: 0, borderRadius: "50%" }}
                        onClick={() => handleRemoveImage("signImage")}
                      >
                        <RiDeleteBinLine className="font-size-11" />
                      </Button>
                    </div>
                  ) : (
                    <label className="btn btn-outline-secondary btn-sm font-size-11 mb-0 w-100 d-flex align-items-center justify-content-center gap-1 cursor-pointer">
                      <RiUpload2Line /> {t("Upload Stamp")}
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleImageUpload(e, "signImage")} />
                    </label>
                  )}
                  <small className="text-muted font-size-10 d-block mt-1">Cashier round stamp</small>
                </Col>
              </Row>
            </div>

            <FormGroup>
              <Label>{t("Receipt Header Title")}</Label>
              <Input
                type="text"
                value={config.header_title || config.headerTitle || ""}
                onChange={(e) => setConfig({ ...config, header_title: e.target.value, headerTitle: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>{t("Acknowledgement Thanks Note")}</Label>
              <Input
                type="textarea"
                rows={3}
                value={config.notes || config.thanksNote || ""}
                onChange={(e) => setConfig({ ...config, notes: e.target.value, thanksNote: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>{t("Theme Accent Color")}</Label>
              <div className="d-flex align-items-center gap-2.5 flex-wrap mt-1">
                {COLOR_PALETTES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className="btn p-0 rounded-circle border shadow-2xs position-relative transition-all"
                    style={{
                      width: "28px",
                      height: "28px",
                      background: c.value,
                      transform: (accentColor === c.value) ? "scale(1.2)" : "scale(1)",
                      border: (accentColor === c.value) ? "2px solid #0f172a" : "1px solid #cbd5e1",
                    }}
                    onClick={() => setConfig({ ...config, accent_color: c.value, accentColor: c.value })}
                    title={c.name}
                  >
                    {(accentColor === c.value) && (
                      <RiCheckLine className="text-white position-absolute" style={{ top: "4px", left: "4px" }} />
                    )}
                  </button>
                ))}
                <div
                  className="position-relative rounded-circle overflow-hidden border shadow-2xs d-flex align-items-center justify-content-center ms-1"
                  style={{
                    width: "28px",
                    height: "28px",
                    minWidth: "28px",
                    minHeight: "28px",
                    background: accentColor,
                    border: "1px solid #cbd5e1",
                    cursor: "pointer",
                  }}
                  title="Custom Color"
                >
                  <input
                    type="color"
                    className="position-absolute"
                    style={{
                      opacity: 0,
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                    }}
                    value={accentColor}
                    onChange={(e) => setConfig({ ...config, accent_color: e.target.value, accentColor: e.target.value })}
                  />
                  <RiPaletteLine className="text-white font-size-14 pointer-events-none" style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.6))" }} />
                </div>
              </div>
            </FormGroup>

            <div className="d-flex gap-2 mt-4">
              <Button color="primary" size="sm" className="flex-grow-1 font-size-12 font-weight-semibold shadow-sm py-2" onClick={handleSave}>
                <RiSaveLine className="me-1" /> {t("Save Receipt Settings")}
              </Button>
              <Button color="light" size="sm" className="border px-3 py-2 font-size-12" onClick={handleTestPrint}>
                <RiPrinterLine className="me-1" /> {t("Test Print")}
              </Button>
            </div>
          </div>
        </Col>

        {/* Live Canvas Simulator */}
        <Col lg={7} md={12}>
          <div className="bg-white border rounded-3 p-3 shadow-2xs">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 bg-light p-2.5 rounded border">
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-dark text-white font-size-11 px-2.5 py-1">
                  <RiEyeLine className="me-1" /> {t("Live Document Preview")}
                </span>
                <span className="badge bg-white text-muted border font-size-11">Voucher Aspect</span>
              </div>
              <div className="btn-group btn-group-sm">
                <Button color={zoomLevel === 85 ? "primary" : "light"} className="font-size-11 py-1 px-2.5" onClick={() => setZoomLevel(85)}>
                  85%
                </Button>
                <Button color={zoomLevel === 100 ? "primary" : "light"} className="font-size-11 py-1 px-2.5" onClick={() => setZoomLevel(100)}>
                  100%
                </Button>
              </div>
            </div>

            <div className="paper-canvas-outer">
              <div
                className="paper-sheet"
                style={{
                  borderTop: `5px solid ${accentColor}`,
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: "top center",
                  minHeight: "520px",
                }}
              >
                <div className="d-flex flex-column justify-content-between h-100 position-relative">
                  <div>
                    {/* Header */}
                    <div className="text-center border-bottom pb-3 mb-3">
                      {config.logoImage ? (
                        <img
                          src={config.logoImage}
                          alt="Clinic Logo"
                          className="mb-2"
                          style={{ maxHeight: "42px", maxWidth: "140px", objectFit: "contain" }}
                        />
                      ) : (
                        <div className="d-inline-flex align-items-center gap-1 px-2.5 py-0.5 mb-1.5 rounded border border-dashed text-muted font-size-10 bg-light">
                          <RiImageAddLine className="font-size-12 text-primary" />
                          <span>Clinic Logo Space</span>
                        </div>
                      )}
                      <h4 className="font-size-18 font-weight-bold mb-0.5" style={{ color: accentColor }}>
                        {clinic?.clinic_name || "909 Dental OPD & Implant Center"}
                      </h4>
                      <div className="font-size-11 font-weight-semibold text-secondary">{config.tagline}</div>
                      <div className="badge bg-light text-dark border font-size-11 px-3 py-1 mt-2">
                        {config.header_title || config.headerTitle}
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="d-flex justify-content-between font-size-11 mb-3">
                      <div>
                        <span className="text-muted">Receipt No: </span>
                        <strong className="text-dark">#REC-909-402</strong>
                      </div>
                      <div>
                        <span className="text-muted">Date: </span>
                        <strong className="text-dark">{new Date().toLocaleDateString("en-GB")}</strong>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="bg-light p-3 rounded border mb-3 font-size-11 d-flex flex-column gap-2">
                      <div>
                        <span className="text-muted">Received With Thanks From:</span>
                        <div className="font-weight-bold text-dark font-size-13">Mrs. Sunita Reddy (Case #909-P-101)</div>
                      </div>
                      <div>
                        <span className="text-muted">Towards:</span>
                        <div className="text-dark">{config.notes || config.thanksNote}</div>
                      </div>
                      <div>
                        <span className="text-muted">Payment Mode:</span>
                        <div className="font-weight-semibold text-dark">UPI (GPay / PhonePe) • Reference: INV-DEMO-2026</div>
                      </div>
                      <div className="border-top pt-2 d-flex justify-content-between align-items-center">
                        <span className="font-weight-bold text-dark">Amount Received:</span>
                        <span className="font-size-16 font-weight-bold" style={{ color: accentColor }}>
                          ₹17,000.00
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-top pt-3 d-flex justify-content-between align-items-end font-size-11">
                    <div className="text-muted" style={{ maxWidth: "320px", whiteSpace: "pre-line" }}>
                      {config.footer_note || config.footerNote}
                    </div>
                    <div className="text-center d-flex flex-column align-items-center">
                      {config.signImage ? (
                        <img
                          src={config.signImage}
                          alt="Cashier Stamp"
                          style={{ maxHeight: "75px", maxWidth: "170px", height: "65px", objectFit: "contain", marginBottom: "4px" }}
                        />
                      ) : (
                        <div className="d-inline-flex flex-column align-items-center justify-content-center p-1.5 mb-1 rounded border border-dashed text-muted font-size-10 bg-light" style={{ width: "160px", height: "52px", borderColor: "#cbd5e1" }}>
                          <span className="font-weight-semibold" style={{ fontSize: "10px" }}>Cashier Stamp & Sign</span>
                          <span style={{ fontSize: "8.5px", opacity: 0.75 }}>Upload via Settings</span>
                        </div>
                      )}
                      <div className="border-top pt-1 font-weight-bold text-dark" style={{ width: "160px", marginTop: config.signImage ? "0" : "4px" }}>
                        {config.signatory_title || config.signatoryTitle || "Reception Desk"}
                      </div>
                      <div className="font-size-10 text-muted">Cashier Stamp</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ManageReceiptTemplate;
